import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, ArrowLeft, LogOut, Trash2, UserCheck, Edit } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'limited'
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    } else if (user) {
      fetchAuthorizedUsers();
    }
  }, [user, loading, navigate]);

  const fetchAuthorizedUsers = async () => {
    try {
      const response = await axios.get(`${API}/authorized-users`, { withCredentials: true });
      setAuthorizedUsers(response.data);
    } catch (error) {
      console.error('Error fetching authorized users:', error);
      toast.error('Failed to load authorized users');
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const openDialog = () => {
    setEditingUser(null);
    setFormData({ email: '', name: '', role: 'limited' });
    setIsDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({ 
      email: user.email, 
      name: user.name || '', 
      role: user.role || 'limited' 
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingUser(null);
    setFormData({ email: '', name: '', role: 'limited' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      if (editingUser) {
        // Update existing user
        await axios.put(`${API}/authorized-users/${editingUser.id}`, formData, { withCredentials: true });
        toast.success(`${formData.email} privileges updated successfully`);
      } else {
        // Add new user
        await axios.post(`${API}/authorized-users`, formData, { withCredentials: true });
        toast.success(`${formData.email} has been authorized`);
      }
      closeDialog();
      fetchAuthorizedUsers();
    } catch (error) {
      console.error('Error adding authorized user:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else if (error.response?.status === 422) {
        // Validation error - show detailed message
        const details = error.response.data.detail;
        if (Array.isArray(details)) {
          const messages = details.map(d => d.msg).join(', ');
          toast.error(`Validation error: ${messages}`);
        } else {
          toast.error(`Validation error: ${details}`);
        }
      } else {
        toast.error(`Failed to add authorized user: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const handleDelete = async (userId, email) => {
    const confirmMessage = `Remove access for "${email}"?\n\nThey will no longer be able to access the admin panel.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/authorized-users/${userId}`, { withCredentials: true });
      toast.success(`Access removed for ${email}`);
      fetchAuthorizedUsers();
    } catch (error) {
      console.error('Error removing authorized user:', error);
      toast.error('Failed to remove user access');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-logo-section" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png" 
              alt="Rebel Angels" 
              className="admin-brand-logo"
            />
          </div>
          <div className="admin-user-section">
            <img src={user.picture} alt={user.name} className="admin-user-avatar" />
            <span className="admin-user-name">{user.name}</span>
            <Button variant="outline" className="admin-logout-button" onClick={handleLogout}>
              <LogOut className="button-icon" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          <div className="admin-page-header">
            <Button className="admin-add-button" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="button-icon" />
              Back to Dashboard
            </Button>
          </div>

          <div className="admin-title-row">
            <h1 className="admin-page-title">Authorized Users</h1>
            <Button className="admin-add-button" onClick={openDialog}>
              <Plus className="button-icon" />
              Add User
            </Button>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <Card style={{ background: 'rgba(229, 0, 0, 0.1)', border: '1px solid var(--retro-red)' }}>
              <CardContent style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <UserCheck style={{ color: 'var(--retro-red)' }} size={20} />
                  <h3 style={{ color: 'var(--retro-red)', margin: 0 }}>Primary Admin</h3>
                </div>
                <p style={{ color: 'var(--retro-cream)', margin: 0, fontSize: '0.9rem' }}>
                  <strong>rebelangelstheband@gmail.com</strong> (Always has access - cannot be removed)
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="admin-users-grid">
            {authorizedUsers.length === 0 ? (
              <p className="admin-empty-message">No additional authorized users. Add someone to give them admin access!</p>
            ) : (
              authorizedUsers.map((authorizedUser) => (
                <Card key={authorizedUser.id} className="admin-user-card">
                  <CardContent className="admin-user-card-content">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h3 className="admin-user-email">{authorizedUser.email}</h3>
                        {authorizedUser.name && (
                          <p className="admin-user-name-display">{authorizedUser.name}</p>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0' }}>
                          <span style={{ 
                            backgroundColor: authorizedUser.role === 'full' ? 'var(--retro-red)' : 'var(--retro-gray)', 
                            color: 'white', 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '12px', 
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            {authorizedUser.role === 'full' ? '🔧 Full Admin' : '📝 Limited Admin'}
                          </span>
                        </div>
                        <p className="admin-user-meta">
                          Added by: {authorizedUser.added_by}<br/>
                          {new Date(authorizedUser.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => openEditDialog(authorizedUser)}
                        >
                          <Edit className="button-icon-sm" />
                          Edit
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => handleDelete(authorizedUser.id, authorizedUser.email)}
                        >
                          <Trash2 className="button-icon-sm" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="admin-dialog">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User Privileges' : 'Add Authorized User'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="admin-form-group">
              <label className="admin-form-label">Email Address *</label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="singer@gmail.com"
              />
              <p style={{ fontSize: '0.8rem', color: 'var(--retro-cream)', opacity: 0.7 }}>
                {editingUser 
                  ? 'Update the email address for this user' 
                  : 'This person will be able to access the admin panel with this email'
                }
              </p>
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Name (Optional)</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Access Level *</label>
              <select 
                value={formData.role} 
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="admin-dropdown"
              >
                <option value="limited">Limited Admin (Shows, Members, Gallery only)</option>
                <option value="full">Full Admin (Everything including User Management)</option>
              </select>
              <p style={{ fontSize: '0.8rem', color: 'var(--retro-cream)', opacity: 0.7, marginTop: '0.25rem' }}>
                Limited admins can manage shows, band members, and gallery but cannot add/remove users
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit">
                {editingUser ? 'Update User' : 'Add User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;